import { createApi } from '@reduxjs/toolkit/query/react'
import { BasePageRequest, BasePageResponse, BaseRequest, BaseResponse } from '../models'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'
import { Scorecard, ScorecardResult } from '../../models/Scorecard'
import { paginate } from '../utils'
import { AiFeatureIntegrationInfo } from '../../models/AiFeature'

// Define a service using a base URL and expected endpoints
const _BASE_URL = import.meta.env.REACT_APP_AI_BASE_URL!

export interface GetLatestScorecardResultForUser extends BaseRequest {
  scorecardId: string
  userId: string
}

export interface GetLatestScorecardResultsForUser extends BaseRequest {
  userId: string
}

export interface GetScorecardResultByFilterRequestBody {
  userId?: string
  scorecardId?: string
  source?: { [key: string]: any }
  field?: string
}

export interface GetScorecardResultByFilterRequest extends BaseRequest {
  body: GetScorecardResultByFilterRequestBody
}

export interface GetScorecardsRequest extends BaseRequest {
  type?: string
  aiFeatureIds?: string
}

export interface GetScorecardsPageRequest extends BasePageRequest {
  type?: string
  aiFeatureIds?: string
}

export interface GetScorecardRequest extends BaseRequest {
  scorecardId: string
}

export interface SaveScorecardResultRequest extends BaseRequest {
  scorecardId: string
  userId: string
  result: { [key: string]: any }
  source: { [key: string]: any }
  previousRevision: number
}

export interface CreateScorecardRequest extends BaseRequest {
  payload: CreateScorecard
}

export interface UpdateScorecardRequest extends BaseRequest {
  scorecardId: string
  payload: UpdateScorecard
}

export interface DeleteScorecardRequest extends BaseRequest {
  scorecardId: string
}

export interface UpdateScorecard {
  name: string
  description: string
  descriptor: object
  config?: ScorecardConfig
  integrationInfos?: AiFeatureIntegrationInfo[] | null
}

export interface CreateScorecard {
  name: string
  description: string
  descriptor: { [key: string]: any }
  type: string
  config?: ScorecardConfig
  integrationInfos?: AiFeatureIntegrationInfo[] | null
}

export interface ScorecardConfig {
  userLevel?: boolean
  includeAdditionalSources?: boolean
}

export const scorecardApi = createApi({
  reducerPath: 'scorecardApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  tagTypes: ['Scorecard', 'ScorecardResult'],
  endpoints: (builder) => ({
    getLatestScorecardResultForUser: builder.query<ScorecardResult, GetLatestScorecardResultForUser>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/scorecards/${request.scorecardId}/results/latest`,
          params: {
            userId: request.userId,
          },
        }
      },
      transformResponse: (response: BaseResponse<ScorecardResult>) => {
        return response.data
      },
      providesTags: ['ScorecardResult'],
    }),
    getLatestScorecardResultsForUser: builder.query<ScorecardResult[], GetLatestScorecardResultsForUser>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/scorecard-results/latest`,
          params: {
            userId: request.userId,
          },
        }
      },
      transformResponse: (response: BaseResponse<ScorecardResult[]>) => {
        return response.data
      },
      providesTags: ['ScorecardResult'],
    }),
    getAllScorecards: builder.query<BaseResponse<Scorecard[]>, GetScorecardsRequest>({
      async queryFn(request, _queryApi, _extraOptions, fetchWithBQ) {
        const { accountId, type, aiFeatureIds } = request
        return await paginate<Scorecard>(
          {
            url: `/accounts/${accountId}/scorecards/`,
            params: {
              aiFeatureIds,
              type,
            },
          },
          fetchWithBQ,
        )
      },
      providesTags: ['Scorecard'],
    }),
    getScorecards: builder.query<BasePageResponse<Scorecard[]>, GetScorecardsPageRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/scorecards/`,
          params: {
            aiFeatureIds: request.aiFeatureIds,
            type: request.type,
            page: request.pageRequest.page,
            size: request.pageRequest.size,
          },
        }
      },
      providesTags: ['Scorecard'],
    }),
    getScorecardById: builder.query<BaseResponse<Scorecard>, GetScorecardRequest>({
      query: (request) => `/accounts/${request.accountId}/scorecards/${request.scorecardId}`,
    }),
    saveScorecardResult: builder.mutation<void, SaveScorecardResultRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/scorecard-results/`,
          method: 'POST',
          body: request,
        }
      },
      invalidatesTags: ['ScorecardResult'],
    }),
    getScorecardResultsByFilter: builder.query<ScorecardResult[], GetScorecardResultByFilterRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/scorecard-results/filter`,
          method: 'POST',
          body: request.body,
        }
      },
      transformResponse: (response: BaseResponse<ScorecardResult[]>) => {
        return response.data
      },
      providesTags: ['ScorecardResult'],
    }),
    createScorecard: builder.mutation<BaseResponse<Scorecard>, CreateScorecardRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/scorecards/`,
          method: 'POST',
          body: request.payload,
        }
      },
      invalidatesTags: ['Scorecard'],
    }),
    deleteScorecard: builder.mutation<void, DeleteScorecardRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/scorecards/${request.scorecardId}`,
          method: 'DELETE',
        }
      },
      invalidatesTags: ['Scorecard'],
    }),
    updateScorecard: builder.mutation<void, UpdateScorecardRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/scorecards/${request.scorecardId}`,
          method: 'PATCH',
          body: request.payload,
        }
      },
      invalidatesTags: ['Scorecard'],
    }),
  }),
})

export const {
  useGetLatestScorecardResultForUserQuery,
  useGetLatestScorecardResultsForUserQuery,
  useGetScorecardsQuery,
  useGetAllScorecardsQuery,
  useLazyGetAllScorecardsQuery,
  useCreateScorecardMutation,
  useDeleteScorecardMutation,
  useUpdateScorecardMutation,
  useSaveScorecardResultMutation,
  useGetScorecardResultsByFilterQuery,
  useLazyGetScorecardResultsByFilterQuery,
  useLazyGetScorecardByIdQuery,
} = scorecardApi
